import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { getConfig } from '@/runtime-config';
import { Button } from '@/components/Button/Button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Language_Options } from '@/constants/language-options';

type LanguageOption = {
  code: string;
  label: string;
};

type LanguageSwitcherProps = {
  languages?: LanguageOption[];
  defaultLanguage?: string;
  onOpenChange?: (open: boolean) => void;
  onLanguageChange?: (code: string) => void;
  /** Show even when the ENABLE_LANGUAGE_SWITCHER flag is off (public signup). */
  alwaysVisible?: boolean;
};

function LanguageSwitcher({
  languages = Language_Options,
  defaultLanguage = 'en',
  onOpenChange,
  onLanguageChange,
  alwaysVisible = false,
}: LanguageSwitcherProps) {
  const { ENABLE_LANGUAGE_SWITCHER } = getConfig();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!ENABLE_LANGUAGE_SWITCHER && !alwaysVisible) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    onLanguageChange?.(lng);
    handleOpenChange(false);
  };

  const currentLang = i18n.language || defaultLanguage;

  const matchedLanguage =
    languages.find((lang) => currentLang.startsWith(lang.code)) ||
    languages.find((lang) => lang.code === defaultLanguage);

  const activeLanguage = matchedLanguage?.label ?? 'English';

  return (
    <Popover modal={false} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="font-normal py-2.5 px-3 leading-0 bg-gray-50"
        >
          <Globe className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-sm p-1">{activeLanguage}</span>
          {open ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-32 p-1 flex flex-col"
        role="listbox"
        aria-label="Select language"
      >
        {languages.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            role="option"
            aria-selected={code === matchedLanguage?.code}
            className="text-left px-3 py-2 rounded-md hover:bg-violet-100 cursor-pointer"
            onClick={() => changeLang(code)}
          >
            {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default LanguageSwitcher;
